import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Switch, FormControlLabel, Grid, Card, CardContent, CardActions,
    IconButton, Chip, Alert, CircularProgress, Tabs, Tab, List, ListItem,
    ListItemText, ListItemSecondaryAction, Divider, Accordion, AccordionSummary,
    AccordionDetails, Fab, Menu, MenuItem
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
    VideoLibrary as VideoIcon, Description as DocumentIcon, CloudUpload as UploadIcon,
    DragHandle as DragIcon, ExpandMore as ExpandMoreIcon, Folder as CategoryIcon,
    Class as ClassIcon, MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';

function NewTrainingManagement() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [classDialogOpen, setClassDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingClass, setEditingClass] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadType, setUploadType] = useState('category'); // 'category' or 'class'
    const [tabValue, setTabValue] = useState(0);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuTarget, setMenuTarget] = useState(null);

    // Category form state
    const [categoryFormData, setCategoryFormData] = useState({
        title: '',
        description: '',
        sort_order: 999,
        is_published: true
    });

    // Class form state
    const [classFormData, setClassFormData] = useState({
        title: '',
        description: '',
        vimeo_video_id: '',
        vimeo_video_url: '',
        vimeo_privacy_hash: '',
        content: '',
        sort_order: 999,
        is_published: true
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/new-training/categories');
            setCategories(response.data.data || []);
        } catch (error) {
            console.error('Error fetching training categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // ===== CATEGORY HANDLERS =====

    const handleOpenCategoryDialog = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setCategoryFormData({
                title: category.title,
                description: category.description || '',
                sort_order: category.sort_order || 999,
                is_published: category.is_published === 1
            });
        } else {
            setEditingCategory(null);
            setCategoryFormData({
                title: '',
                description: '',
                sort_order: 999,
                is_published: true
            });
        }
        setDialogOpen(true);
    };

    const handleSaveCategory = async () => {
        try {
            if (editingCategory) {
                await axios.put(`/api/new-training/categories/${editingCategory.id}`, categoryFormData);
            } else {
                await axios.post('/api/new-training/categories', categoryFormData);
            }
            setDialogOpen(false);
            fetchCategories();
        } catch (error) {
            console.error('Error saving category:', error);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (window.confirm('Are you sure you want to delete this category? This will also delete all classes and documents within it.')) {
            try {
                await axios.delete(`/api/new-training/categories/${categoryId}`);
                fetchCategories();
            } catch (error) {
                console.error('Error deleting category:', error);
            }
        }
    };

    // ===== CLASS HANDLERS =====

    const handleOpenClassDialog = (categoryId, classItem = null) => {
        if (classItem) {
            setEditingClass(classItem);
            setClassFormData({
                title: classItem.title,
                description: classItem.description || '',
                vimeo_video_id: classItem.vimeo_video_id || '',
                vimeo_video_url: classItem.vimeo_video_url || '',
                vimeo_privacy_hash: classItem.vimeo_privacy_hash || '',
                content: classItem.content || '',
                sort_order: classItem.sort_order || 999,
                is_published: classItem.is_published === 1
            });
        } else {
            setEditingClass(null);
            setClassFormData({
                title: '',
                description: '',
                vimeo_video_id: '',
                vimeo_video_url: '',
                vimeo_privacy_hash: '',
                content: '',
                sort_order: 999,
                is_published: true
            });
        }
        setSelectedCategory({ id: categoryId });
        setClassDialogOpen(true);
    };

    const handleSaveClass = async () => {
        try {
            if (editingClass) {
                await axios.put(`/api/new-training/classes/${editingClass.id}`, classFormData);
            } else {
                await axios.post(`/api/new-training/categories/${selectedCategory.id}/classes`, classFormData);
            }
            setClassDialogOpen(false);
            fetchCategories();
        } catch (error) {
            console.error('Error saving class:', error);
        }
    };

    const handleDeleteClass = async (classId) => {
        if (window.confirm('Are you sure you want to delete this class? This will also delete all documents within it.')) {
            try {
                await axios.delete(`/api/new-training/classes/${classId}`);
                fetchCategories();
            } catch (error) {
                console.error('Error deleting class:', error);
            }
        }
    };

    // ===== UPLOAD HANDLERS =====

    const handleOpenUploadDialog = (type, target) => {
        setUploadType(type);
        if (type === 'category') {
            setSelectedCategory(target);
        } else {
            setSelectedClass(target);
        }
        setSelectedFiles([]);
        setUploadDialogOpen(true);
    };

    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) return;

        const formData = new FormData();
        for (let i = 0; i < selectedFiles.length; i++) {
            formData.append('documents', selectedFiles[i]);
        }

        try {
            let url;
            if (uploadType === 'category') {
                url = `/api/new-training/categories/${selectedCategory.id}/documents`;
            } else {
                url = `/api/new-training/classes/${selectedClass.id}/documents`;
            }
            
            await axios.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setUploadDialogOpen(false);
            fetchCategories();
        } catch (error) {
            console.error('Error uploading files:', error);
        }
    };

    const handleDeleteDocument = async (documentId, type, parentId) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            try {
                let url;
                if (type === 'category') {
                    url = `/api/new-training/categories/${parentId}/documents/${documentId}`;
                } else {
                    url = `/api/new-training/classes/${parentId}/documents/${documentId}`;
                }
                
                await axios.delete(url);
                fetchCategories();
            } catch (error) {
                console.error('Error deleting document:', error);
            }
        }
    };

    // ===== VIEW HANDLERS =====

    const handleViewClass = async (classId) => {
        try {
            const response = await axios.get(`/api/new-training/classes/${classId}`);
            setSelectedClass(response.data.data);
            setViewDialogOpen(true);
        } catch (error) {
            console.error('Error fetching class details:', error);
        }
    };

    const toggleCategoryExpansion = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const handleMenuOpen = (event, target) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuTarget(target);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuTarget(null);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') return '📄';
        if (mimeType.includes('word')) return '📝';
        if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📊';
        if (mimeType.includes('excel') || mimeType.includes('sheet')) return '📈';
        if (mimeType.startsWith('image/')) return '🖼️';
        return '📁';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" display="flex" alignItems="center">
                    <CategoryIcon sx={{ mr: 2 }} />
                    Training Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCategoryDialog()}
                    color="primary"
                >
                    Add Category
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                This is the standalone Training system. It is separate from the "TELUS Sales Manual & Instruction" system.
            </Alert>

            {categories.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No training categories created yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Start by creating your first training category to organize your content.
                    </Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => handleOpenCategoryDialog()}
                    >
                        Create First Category
                    </Button>
                </Paper>
            ) : (
                <Box>
                    {categories.map((category) => (
                        <Accordion
                            key={category.id}
                            expanded={expandedCategories[category.id] || false}
                            onChange={() => toggleCategoryExpansion(category.id)}
                            sx={{ mb: 2, '&:before': { display: 'none' } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <CategoryIcon />
                                        <Box>
                                            <Typography variant="h6">{category.title}</Typography>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip 
                                                    label={`${category.class_count || 0} classes`}
                                                    size="small" 
                                                    color="primary"
                                                />
                                                <Chip 
                                                    label={`${category.documents?.length || 0} documents`}
                                                    size="small" 
                                                    color="secondary"
                                                />
                                                <Chip 
                                                    label={category.is_published ? 'Published' : 'Draft'}
                                                    size="small" 
                                                    color={category.is_published ? 'success' : 'default'}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuOpen(e, { type: 'category', data: category });
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box>
                                    {category.description && (
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            {category.description}
                                        </Typography>
                                    )}

                                    {/* Category Documents */}
                                    {category.documents && category.documents.length > 0 && (
                                        <Box mb={3}>
                                            <Typography variant="h6" gutterBottom>
                                                Category Resources ({category.documents.length})
                                            </Typography>
                                            <List dense>
                                                {category.documents.map((doc) => (
                                                    <ListItem key={doc.id}>
                                                        <Box display="flex" alignItems="center" mr={2}>
                                                            <Typography sx={{ fontSize: '1.2em', mr: 1 }}>
                                                                {getFileIcon(doc.mime_type)}
                                                            </Typography>
                                                        </Box>
                                                        <ListItemText
                                                            primary={doc.original_name}
                                                            secondary={
                                                                <Typography variant="caption">
                                                                    {formatFileSize(doc.file_size)} • {doc.mime_type.split('/')[1].toUpperCase()}
                                                                </Typography>
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                color="error"
                                                                onClick={() => handleDeleteDocument(doc.id, 'category', category.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {/* Classes */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6">
                                            Training Classes ({category.classes?.length || 0})
                                        </Typography>
                                        <Box>
                                            <Button
                                                size="small"
                                                startIcon={<UploadIcon />}
                                                onClick={() => handleOpenUploadDialog('category', category)}
                                                sx={{ mr: 1 }}
                                            >
                                                Add Documents
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleOpenClassDialog(category.id)}
                                            >
                                                Add Class
                                            </Button>
                                        </Box>
                                    </Box>

                                    {category.classes && category.classes.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {category.classes.map((cls) => (
                                                <Grid item xs={12} md={6} lg={4} key={cls.id}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Typography variant="h6" gutterBottom noWrap>
                                                                {cls.title}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                                {cls.description || 'No description provided'}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                                {cls.vimeo_video_id && (
                                                                    <Chip 
                                                                        icon={<VideoIcon />} 
                                                                        label="Video" 
                                                                        size="small" 
                                                                        color="success"
                                                                    />
                                                                )}
                                                                {cls.document_count > 0 && (
                                                                    <Chip 
                                                                        icon={<DocumentIcon />} 
                                                                        label={`${cls.document_count} docs`} 
                                                                        size="small" 
                                                                        color="secondary"
                                                                    />
                                                                )}
                                                                <Chip 
                                                                    label={cls.is_published ? 'Published' : 'Draft'}
                                                                    size="small" 
                                                                    color={cls.is_published ? 'success' : 'default'}
                                                                />
                                                            </Box>
                                                        </CardContent>
                                                        <CardActions>
                                                            <Button
                                                                size="small"
                                                                startIcon={<ViewIcon />}
                                                                onClick={() => handleViewClass(cls.id)}
                                                            >
                                                                View
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                startIcon={<EditIcon />}
                                                                onClick={() => handleOpenClassDialog(category.id, cls)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                startIcon={<UploadIcon />}
                                                                onClick={() => handleOpenUploadDialog('class', cls)}
                                                            >
                                                                Docs
                                                            </Button>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteClass(cls.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Box textAlign="center" py={3}>
                                            <ClassIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                No classes in this category yet
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Context Menu */}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
                {menuTarget?.type === 'category' && (
                    <div>
                        <MenuItem onClick={() => { handleOpenCategoryDialog(menuTarget.data); handleMenuClose(); }}>
                            <EditIcon sx={{ mr: 1 }} /> Edit Category
                        </MenuItem>
                        <MenuItem onClick={() => { handleDeleteCategory(menuTarget.data.id); handleMenuClose(); }}>
                            <DeleteIcon sx={{ mr: 1 }} /> Delete Category
                        </MenuItem>
                    </div>
                )}
            </Menu>

            {/* Category Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Create New Category'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Category Title"
                            value={categoryFormData.title}
                            onChange={(e) => setCategoryFormData({ ...categoryFormData, title: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={categoryFormData.description}
                            onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Sort Order"
                                    type="number"
                                    value={categoryFormData.sort_order}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, sort_order: parseInt(e.target.value) || 999 })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={categoryFormData.is_published}
                                            onChange={(e) => setCategoryFormData({ ...categoryFormData, is_published: e.target.checked })}
                                        />
                                    }
                                    label="Published"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveCategory} variant="contained">
                        {editingCategory ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Class Dialog */}
            <Dialog open={classDialogOpen} onClose={() => setClassDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editingClass ? 'Edit Class' : 'Create New Class'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                            <Tab label="Basic Info" />
                            <Tab label="Video & Content" />
                            <Tab label="Settings" />
                        </Tabs>

                        {tabValue === 0 && (
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Class Title"
                                    value={classFormData.title}
                                    onChange={(e) => setClassFormData({ ...classFormData, title: e.target.value })}
                                    margin="normal"
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={classFormData.description}
                                    onChange={(e) => setClassFormData({ ...classFormData, description: e.target.value })}
                                    margin="normal"
                                    multiline
                                    rows={3}
                                />
                            </Box>
                        )}

                        {tabValue === 1 && (
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Vimeo Video ID"
                                            value={classFormData.vimeo_video_id}
                                            onChange={(e) => setClassFormData({ ...classFormData, vimeo_video_id: e.target.value })}
                                            margin="normal"
                                            placeholder="e.g., 123456789"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Vimeo Video URL"
                                            value={classFormData.vimeo_video_url}
                                            onChange={(e) => setClassFormData({ ...classFormData, vimeo_video_url: e.target.value })}
                                            margin="normal"
                                            placeholder="https://vimeo.com/..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Privacy Hash"
                                            value={classFormData.vimeo_privacy_hash}
                                            onChange={(e) => setClassFormData({ ...classFormData, vimeo_privacy_hash: e.target.value })}
                                            margin="normal"
                                            placeholder="For private videos"
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Class Content
                                    </Typography>
                                    <Editor
                                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY || "cidq6j1ldl24ah599vsr9xb1rfl9ewlslc2cc1f3xoetd6zn"}
                                        value={classFormData.content}
                                        onEditorChange={(content) => setClassFormData({ ...classFormData, content })}
                                        init={{
                                            height: 400,
                                            menubar: false,
                                            plugins: [
                                                'advlist autolink lists link image charmap print preview anchor',
                                                'searchreplace visualblocks code fullscreen',
                                                'insertdatetime media table paste code help wordcount'
                                            ],
                                            toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {tabValue === 2 && (
                            <Box>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Sort Order"
                                            type="number"
                                            value={classFormData.sort_order}
                                            onChange={(e) => setClassFormData({ ...classFormData, sort_order: parseInt(e.target.value) || 999 })}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={classFormData.is_published}
                                                    onChange={(e) => setClassFormData({ ...classFormData, is_published: e.target.checked })}
                                                />
                                            }
                                            label="Published"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClassDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveClass} variant="contained">
                        {editingClass ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Dialog */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Upload Documents to {uploadType === 'category' ? 'Category' : 'Class'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                            onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                            style={{ width: '100%', marginBottom: '16px' }}
                        />
                        {selectedFiles.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Selected Files ({selectedFiles.length}):
                                </Typography>
                                <List dense>
                                    {Array.from(selectedFiles).map((file, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={formatFileSize(file.size)}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleFileUpload} 
                        variant="contained" 
                        disabled={selectedFiles.length === 0}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Class Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {selectedClass?.title}
                </DialogTitle>
                <DialogContent>
                    {selectedClass && (
                        <Box>
                            {selectedClass.description && (
                                <Typography variant="body1" paragraph>
                                    {selectedClass.description}
                                </Typography>
                            )}
                            
                            {selectedClass.vimeo_video_id && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>Training Video</Typography>
                                    <Box sx={{ position: 'relative', width: '100%', height: 0, paddingBottom: '56.25%' }}>
                                        <iframe
                                            src={`https://player.vimeo.com/video/${selectedClass.vimeo_video_id}${selectedClass.vimeo_privacy_hash ? `?h=${selectedClass.vimeo_privacy_hash}&` : '?'}title=0&byline=0&portrait=0&badge=0&autopause=0`}
                                            frameBorder="0"
                                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%'
                                            }}
                                            allowFullScreen
                                            title={selectedClass.title}
                                        />
                                    </Box>
                                </Box>
                            )}
                            
                            {selectedClass.content && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>Class Materials</Typography>
                                    <div dangerouslySetInnerHTML={{ __html: selectedClass.content }} />
                                </Box>
                            )}
                            
                            {selectedClass.documents && selectedClass.documents.length > 0 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Class Documents ({selectedClass.documents.length})
                                    </Typography>
                                    <List>
                                        {selectedClass.documents.map((doc) => (
                                            <ListItem key={doc.id}>
                                                <Box display="flex" alignItems="center" mr={2}>
                                                    <Typography sx={{ fontSize: '1.2em', mr: 1 }}>
                                                        {getFileIcon(doc.mime_type)}
                                                    </Typography>
                                                </Box>
                                                <ListItemText
                                                    primary={doc.original_name}
                                                    secondary={
                                                        <Typography variant="caption">
                                                            {formatFileSize(doc.file_size)} • {doc.mime_type.split('/')[1].toUpperCase()}
                                                        </Typography>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => window.open(`/api/new-training/classes/${selectedClass.id}/documents/${doc.id}/download`)}
                                                    >
                                                        <DocumentIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default NewTrainingManagement;